/* ++++++++++ --------------- IMPORTS --------------- ++++++++++ */
// libraries
import React, { useCallback, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
// redux
import { createPaymentInformation, fetchPaymentInformation, updatePaymentInformation } from '../../../../../../state/thunks/account';
import { validation } from '../../../../../../utils/validation';
// const
import { paymentMethodLabel, formDefault, saveFormResultMessage, paymentUUID } from '../payment_const';
// material
import {
  InlineMessage, Group, Copy, Title, Link, UnsavedMessage, Button, Section, PopupMessage,
  displayModal, displaySuccessMessage, displayErrorMessage, sendMessage
} from '@insticator/insticator-ui'; // iui-materials
import Paypal from './paypal/paypal';
import DirectDeposit from './directdeposit/directdeposit';
import PreferSelector from './preferselector/preferselector';
// styles
import injectSheet from 'react-jss';
import styles from './paymentinfo_styles';
import Check from './check/check';
import WireTransfer from './wiretransfer/wiretransfer';

/* ========== ~~~~~~~~~~ PAYMENT INFO ~~~~~~~~~~ ========== */
const PaymentInfo = injectSheet(styles)((props) => {  
    const dispatch = useDispatch()

    const { session: { accountUUID }} = useSelector(state => state)

    const { type, payment, payoutThreshold, preferredPaymentType, moveBack } = props

    const [editable, setEditable] = useState(!payment ? true : false)
    const [isSaveClicked, setSaveClicked] = useState(false)

    const [isSaving, setIsSaving] = useState(false)
    const [formDetails, setFormDetails] = useState(preferredPaymentType ? formDefault[type] : {...formDefault[type], preferredPayment: true}) // if no preferred payment set it to true

    const handleFormChange = (name, value) => setFormDetails({...formDetails, [name]: value})

    const handleFormFileUpload = (file) => {
      // TODO: Refactor this for other payment form file types
      if(['ACH', 'CHECK'].includes(type)) {
        setFormDetails({
          ...formDetails,
          w9Form: file,
          w9FormName: file.name,
          w9FormLink: null
        })
      }
      if(type === 'WIRE') {
        setFormDetails({
          ...formDetails,
          w8Form: file,
          w8FormName: file.name
        })
      }
    }

    const handleFormFileDiscard = () => {
      // TODO: Refactor this for other payment form file types
      if(['ACH', 'CHECK'].includes(type)) {
        setFormDetails({
          ...formDetails,
          w9Form: null,
          w9FormName: '',
          w9FormUpdateDateTime: ''
        })
      }
      if(type === 'WIRE') {
        setFormDetails({
          ...formDetails,
          w8Form: null,
          w8FormName: '',
          w8BENFormLink: null,
          w8BENFormName: '',
          w8BENFormUpdateDateTime: '',
          w8BENEFormLink: null,
          w8BENEFormName: '',
          w8BENEFormUpdateDateTime: ''
        })
      }
    }

    const onPreferredMethodChange = value => {
      setFormDetails({...formDetails, preferredPayment: value})
      if (type !== preferredPaymentType) 
        dispatch(sendMessage('After checking this box and saving, all payments from now on will be made through this new payment method.'))
    }

    const handleBack = () => editable ? dispatch(displayModal(true, <UnsavedMessage primaryAction={moveBack} />)) : moveBack();

    const formatRequestData = paymentType => {
      switch(paymentType) {
        case 'PAYPAL':
          return { 
            paypalUserName: formDetails.paypalUserName, 
            preferredPayment: formDetails.preferredPayment
          }
        default:
          return {...formDetails}
      }
    }
    
    const verifyPreferredPaymentMethod = () => {
      if(type !== preferredPaymentType && formDetails.preferredPayment) handleSave(true) 
      else if(preferredPaymentType === type) handleSave(true) 
      else {
        dispatch(displayModal(true, <PopupMessage
            type={'verify'}
            title={'Verify your preferred payment method'}
            body={<p>Your current preferred payment method is <strong>{preferredPaymentType}</strong>. Do you want <strong>{type}</strong> to be your new preferred method?</p>}
            buttonPrimary={{ text: 'Yes, confirm new payment method', variant: 'action', action: () => handleSave(true) }}
            buttonSecondary={{ text: 'Save without changing preferred method', variant: 'system', action: () => handleSave(false) }}
        />));
      }
    }

    const handleSave = (isPaymentPreferred) => {
      setIsSaving(true)
      let data = {}
      if(payment) {
        // update existing payment
        data = {...formatRequestData(payment.paymentType), preferredPayment: isPaymentPreferred}
        const uuidKey = paymentUUID[payment.paymentType]
        requestUpdatePaymentInfo(data, payment.paymentType, payment[uuidKey])
      } else {
        // add new payment
        data = {...formatRequestData(type), preferredPayment: isPaymentPreferred}
        requestCreatePaymentInfo(data, type, accountUUID)
      }
    }

    const requestCreatePaymentInfo = useCallback(async (data, paymentType, accountUUID) => {
      const response = await dispatch(createPaymentInformation({data, paymentType, accountUUID}))
      if(response.payload) {
        setIsSaving(false)
        dispatch(displaySuccessMessage(saveFormResultMessage[paymentType].success))
        const response = await dispatch(fetchPaymentInformation(accountUUID))
        if(response.payload || response.data) moveBack('EXISTINGPAYMENTS')
      }
      else {
        setIsSaving(false)
        dispatch(displayErrorMessage(saveFormResultMessage[paymentType].error))
      }
    }, [dispatch, moveBack])

    const requestUpdatePaymentInfo = useCallback(async (data, paymentType, paymentUUID) => {
      const response = await dispatch(updatePaymentInformation({data, paymentType, paymentUUID}))
      if(response.payload) {
        setIsSaving(false)
        dispatch(displaySuccessMessage(saveFormResultMessage[paymentType].success))
        const response = await dispatch(fetchPaymentInformation(accountUUID))
        moveBack()
      }
      else {
        setIsSaving(false)
        dispatch(displayErrorMessage(saveFormResultMessage[paymentType].error))
      }
    }, [dispatch, moveBack, accountUUID])


    const renderPaymentInfoForm = () => {
      switch(type) {
        case 'PAYPAL':
          return <Paypal
            details={formDetails} 
            setDetails={setFormDetails} 
            onFormChange={handleFormChange}
            paypalUUID={payment?.paypalUUID || null} 
            editable={editable} 
            isSaveClicked={isSaveClicked}
          />
        case 'ACH':
          return <DirectDeposit 
            details={formDetails} 
            setDetails={setFormDetails} 
            onFormChange={handleFormChange}
            achUUID={payment?.achUUID || null} 
            onFormFileUpload={handleFormFileUpload}
            onFormFileDiscard={handleFormFileDiscard}
            editable={editable} 
            isSaveClicked={isSaveClicked}
          />
        case 'CHECK':
          return <Check
            details={formDetails} 
            setDetails={setFormDetails} 
            onFormChange={handleFormChange}
            checkUUID={payment?.checkUUID || null} 
            onFormFileUpload={handleFormFileUpload}
            onFormFileDiscard={handleFormFileDiscard}
            editable={editable} 
            isSaveClicked={isSaveClicked}
          />
        case 'WIRE': 
          return <WireTransfer 
            details={formDetails} 
            setDetails={setFormDetails} 
            onFormChange={handleFormChange}
            wireTransferUUID={payment?.wireTransferUUID || null} 
            onFormFileUpload={handleFormFileUpload}
            onFormFileDiscard={handleFormFileDiscard}
            editable={editable} 
            isSaveClicked={isSaveClicked}
          />
        default:
          break;
      }
    }

    const renderFormDownloadNotification = () => {
      const linkToForm = () => {
        const isInternationCorporation = formDetails.businessType === 'INTERNATIONAL_CORPORATION'
        if(formDetails.w8BENEFormLink || formDetails.w8BENFormLink) {
          if(isInternationCorporation) return formDetails.w8BENEFormLink
          else return formDetails.w8BENFormLink
        } else {
          return isInternationCorporation ?  `https://www.irs.gov/pub/irs-pdf/fw8bene.pdf` : `https://www.irs.gov/pub/irs-pdf/fw8ben.pdf`
        }
      }
      
      if (!editable) {
        if (type === 'ACH' || type === 'CHECK') {
          return <InlineMessage name={'downloadnotifmsg'} icon={`info-circle`}
                              message={<p>Download your W9 Form <strong><Link type={'external'}
                                                                            action={formDetails.w9FormLink ? formDetails.w9FormLink : 'https://www.irs.gov/pub/irs-pdf/fw9.pdf'}
                                                                            text={'here'}/></strong></p>}
                              variant={`system`} size={`half`}/>
        } else if(type === 'WIRE') {
          return <InlineMessage name={'downloadnotifmsg'} icon={`info-circle`} message={<p>Download
              your {formDetails.businessType === `INTERNATIONAL_CORPORATION` ? `W-8BEN-E Form` : `W-8BEN Form`} Form <strong><Link
                type={'external'} action={linkToForm()} text={'here'}/></strong>
          </p>} variant={`system`} size={`half`}/>
        }
      }
    };

    const handleSaveClick = () => {
      setSaveClicked(true)
      return disableSave ? null : verifyPreferredPaymentMethod()
    }

    const disableSave = useMemo(() => { 
      const isValid = name =>  validation[name].test.test(formDetails[name])

      const selectedBusinessTypeForm = () => {
        if(formDetails.businessType === 'INTERNATIONAL_CORPORATION') return 'w8BENEForm'
        else return 'w8BENForm'
      }

      if(type === 'PAYPAL') return !isValid('paypalUserName')

      if(type === 'ACH') return !(
        formDetails.bankAccountType && isValid('bankName') && isValid('accountName') && 
        isValid('routingCode') && isValid('accountNumber') && !!(formDetails.w9Form || formDetails.w9FormName)
      )

      if(type === 'CHECK') return !(
        isValid('payTo') && isValid('paymentStreetAddress') && isValid('city') && isValid('zipCode') &&
        isValid('state') && isValid('country')&& !!(formDetails.w9Form || formDetails.w9FormName)
      )

      if(type === 'WIRE') return !(
        formDetails.businessType && isValid('bankName') && isValid('accountName') && 
        isValid('bankStreetAddress') && isValid('bankCity') && isValid('bankRegion') && 
        isValid('bankZipCode') && isValid('bankCountry') && 
        isValid('swift') && isValid('iban') &&
        (!!(formDetails.w8Form || formDetails.w8FormName) || !!(formDetails[selectedBusinessTypeForm()] || formDetails[`${selectedBusinessTypeForm()}Name`]))
      )
      return false
    }, [type, formDetails])

    return(
      <div className={`paymentinfo ${props.classes.paymentinfo}`}>
        <Section>
          <Copy name={`page-copy`}>
                <Title variant={'withctas'}>
                    <h2>Payment Method - { paymentMethodLabel[type] } </h2>
                    <Group name={`formaction`}>
                      <Button name={'back'} variant={'system'} text={'Back'} handleClick={handleBack} />
                      { !editable ?
                      <Button name={'edit'} variant={'primary'} text={'Edit this payment information'} handleClick={() => setEditable(true)} />
                      :
                      <Button name={'save'} variant={'primary'} text={'Save'} handleClick={handleSaveClick} processing={isSaving} operation={'save'} />
                      }
                    </Group>
                </Title>
            </Copy>
            <Group name={`payment-select`}>
                <div className={`payment-method`}>
                    <InlineMessage icon={`exclamation-triangle`} message={`Minimum threshold for any payment is $${payoutThreshold || 0}.00 USD`} variant={`neutral`} size={`half`} />
                </div>
            </Group>
            <Group name={`page-details`}>
              { renderFormDownloadNotification() }
              { renderPaymentInfoForm() }
              <PreferSelector 
                onChange={onPreferredMethodChange} 
                editable={editable}
                selectedPaymentType={type}
                preferredPaymentType={preferredPaymentType}
                isPaymentPreferred={formDetails?.preferredPayment} 
              />
            </Group>
        </Section>
      </div>
    );
            
});



/* ++++++++++ --------------- EXPORTS --------------- ++++++++++ */
export default PaymentInfo